import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isV2PatientVersion } from '@odo/shared-ui';
import { useApprovePatient } from '../../../../hooks/useApprovePatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import {
    approveCase as approveCaseV2,
    declineTreatment as declineTreatmentV2,
    requestRevision as requestRevisionV2
} from '../../../../services/patient/approval';
import {
    fetchDxReportOrthopulseStatus,
    fetchDxReportVersions
} from '../../../../services/patient/dxReport';
import { formatPriceCents } from '../../../../utils/currency';
import { ActionFooter } from './ActionFooter';
import { AlignerSection } from './AlignerSection';
import { PatientHeaderCard } from './PatientHeaderCard';
import { ReasonCaptureModal } from './ReasonCaptureModal';
import { ClarificationHistory } from '../../../../components/clarifications/ClarificationHistory';
import { PatientActionPage } from '../shared/PatientActionPage';
import { VersionedPdfPane } from '../shared/VersionedPdfPane';
import type { AlignerOption } from './types';
import {
    INTERNAL_STATUS_PENDING_APPROVAL,
    REQUIRED_ACTION_APPROVE_CASE
} from '../../../../utils/constants';

type ModalKind = 'revision' | 'decline' | null;

const PatientApprove: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: patient, isLoading } = useApprovePatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [aligner, setAligner] = useState<AlignerOption>(null);
    const [addOrthopulse, setAddOrthopulse] = useState(false);
    const [orthopulseLevel, setOrthopulseLevel] = useState<'mandatory' | 'recommended' | null>(
        null
    );
    const [ack, setAck] = useState(false);
    const [modal, setModal] = useState<ModalKind>(null);
    const [submitting, setSubmitting] = useState(false);

    const patientId = patient?.id ?? null;

    // Resolved server-side against the patient's practice (its market and
    // program), so this needs no extra request. Null when the catalog has no
    // matching version, in which case the toggle renders without a price
    // rather than inventing one.
    const orthopulsePriceLabel = useMemo(() => {
        const cents = patient?.orthopulsePriceCents ?? null;
        return cents == null ? null : formatPriceCents(cents);
    }, [patient?.orthopulsePriceCents]);

    const canRequestRevision = patient?.internalCaseStatus === INTERNAL_STATUS_PENDING_APPROVAL;

    const { data: dxOrthopulseStatus } = useQuery({
        queryKey: ['patient', patientId, 'dx-orthopulse-status'],
        queryFn: () => fetchDxReportOrthopulseStatus(patientId!),
        enabled: !!patientId,
        staleTime: 60_000
    });

    const { data: dxVersions } = useQuery({
        queryKey: ['patient', patientId, 'dx-versions'],
        queryFn: () => fetchDxReportVersions(patientId!),
        enabled: !!patientId,
        staleTime: 60_000
    });

    useEffect(() => {
        if (dxOrthopulseStatus) {
            // Canonical Dx-report-driven preset. Mandatory locks the toggle on;
            // recommended/null only set the default-on state and the badge.
            const level =
                dxOrthopulseStatus === 'mandatory' || dxOrthopulseStatus === 'recommended'
                    ? dxOrthopulseStatus
                    : null;
            setOrthopulseLevel(level);
            setAddOrthopulse(level !== null);
            return;
        }
        // Fallback: pre-Dx-report Monday recommendation string. Mirrors
        // mondayRecommendationMarksOrthopulseMandatory in orthopulseMandatory.ts - keep in sync.
        const recommendation = (patient?.orthopulseRecommendation ?? '').trim().toLowerCase();
        const level: 'mandatory' | 'recommended' | null =
            recommendation === 'required' || recommendation === 'mandatory'
                ? 'mandatory'
                : recommendation === 'recommended'
                  ? 'recommended'
                  : null;
        setOrthopulseLevel(level);
        if (level !== null) setAddOrthopulse(true);
    }, [dxOrthopulseStatus, patient]);

    const handleOrthopulseChange = (add: boolean): void => {
        if (orthopulseLevel === 'mandatory' && !add) return;
        setAddOrthopulse(add);
    };

    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;

    const canApprove = aligner !== null && ack && !submitting && modal === null;

    const patientName = useMemo(() => patient?.name ?? '', [patient]);

    const handleApprove = async (): Promise<void> => {
        if (!patientId || !aligner) return;
        setSubmitting(true);
        try {
            const params = { option: aligner, addOrthopulse };
            await approveCaseV2(patientId, params);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
            markActionCompleted(patientId, REQUIRED_ACTION_APPROVE_CASE);
            showToast('Case approved and sent to manufacturing', 'success');
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to approve case';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestRevision = async (note: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await requestRevisionV2(patientId, note);
            // The just-sent revision request is a clarification-loop event. Invalidate
            // the shared ['clarifications', patientId] cache so the next view of
            // ClarificationHistory shows it immediately instead of waiting 60s.
            queryClient.invalidateQueries({ queryKey: ['clarifications', patientId] });
            queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            markActionCompleted(patientId, REQUIRED_ACTION_APPROVE_CASE);
            showToast('Revision request sent to ortho team', 'success');
            setModal(null);
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to send revision request';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDecline = async (reason: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await declineTreatmentV2(patientId, reason);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
            markActionCompleted(patientId, REQUIRED_ACTION_APPROVE_CASE);
            showToast('Case declined', 'success');
            setModal(null);
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to decline case';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle="Approve case"
            tabIdPrefix="approve"
            redirectToOverview={isV2PatientVersion(patient?.version)}
            leftPane={
                <VersionedPdfPane
                    patientId={patientId}
                    versions={dxVersions ?? []}
                    docLabel="Dx report"
                />
            }
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        duration={patient?.treatmentDuration ?? null}
                    />
                    <AlignerSection
                        aligner={aligner}
                        onAlignerChange={setAligner}
                        addOrthopulse={addOrthopulse}
                        onOrthopulseChange={handleOrthopulseChange}
                        ack={ack}
                        onAckChange={setAck}
                        showOrthopulse={true}
                        orthopulsePriceLabel={orthopulsePriceLabel}
                        orthopulseLevel={orthopulseLevel}
                    />
                </>
            }
            footer={
                <ActionFooter
                    canApprove={canApprove}
                    canRequestRevision={canRequestRevision}
                    isSubmitting={submitting}
                    onApprove={() => void handleApprove()}
                    onRequestRevision={() => setModal('revision')}
                    onDecline={() => setModal('decline')}
                />
            }
        >
            <ReasonCaptureModal
                open={modal === 'revision'}
                tone="amber"
                title="Request a revision"
                subtitle={`${patientName || 'Patient'}`}
                noteLabel="Note for the ortho team"
                notePlaceholder="What would you like changed for this case? "
                helperText="The case will be sent back to the ortho team. You'll be notified when a revised Dx report is ready for your review."
                submitLabel="Send revision request"
                isSubmitting={submitting}
                onSubmit={(note) => void handleRequestRevision(note)}
                onClose={() => setModal(null)}
                beforeNote={patientId ? <ClarificationHistory patientId={patientId} /> : undefined}
            />
            <ReasonCaptureModal
                open={modal === 'decline'}
                tone="red"
                title="Decline case"
                subtitle={`${patientName || 'Patient'}`}
                noteLabel="Reason for declining"
                notePlaceholder="e.g. Patient withdrew consent, or treatment is no longer indicated."
                helperText="Declining this case marks it as terminated. this cannot be undone."
                submitLabel="Confirm decline"
                isSubmitting={submitting}
                onSubmit={(reason) => void handleDecline(reason)}
                onClose={() => setModal(null)}
            />
        </PatientActionPage>
    );
};

export default PatientApprove;
